import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ProductResponse } from '../../services/api.service';
import { CartService, CartItem } from '../../services/cart.service';
import { LanguageService } from '../../services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '../../services/currency.service';
import { ProductSheetService } from '../../services/product-sheet.service';
import { SupplementCatalogService } from '../../services/supplement-catalog.service';
import { AnalyticsService } from '../../services/analytics.service';
import { CartSupplement, MenuSupplement, toCartSupplement } from '../../models/supplement.model';
import { cartLineKey, cartLineTotal } from '../../shared/supplement-pricing';

/**
 * What the bottom bar does. Decided ONCE, from `cartAction`, instead of by the three parallel
 * if/else chains this component used to carry (label, colour, click handler) — they had to agree
 * with each other and quietly stopped agreeing the moment edit mode was added.
 */
type CartAction = 'add' | 'update' | 'remove';

@Component({
    selector: 'app-product-details',
    templateUrl: './product-details.component.html',
    styleUrls: ['./product-details.component.scss'],
    standalone: false
})
export class ProductDetailsComponent implements OnInit, OnDestroy {

  product: ProductResponse | null = null;
  loading = false;
  clientData: any = null;
  currencySymbol: string = '';

  cartItems: CartItem[] = [];
  currentLang!: string;

  // Local quantity state for product details
  localQuantity: number = 1;

  /**
   * Whether the number in the stepper is the guest's own, or one inherited from a cart line.
   *
   * The two are identical as integers and want opposite things the moment the selection moves to a
   * combination the order does not hold: a number the guest typed should survive (three pizzas, now
   * with cheese), while one adopted from another combination's line must not silently become the
   * size of an order they never placed.
   *
   * Every write to `localQuantity` sets this too, so the two can never drift apart.
   */
  private quantityIsGuestSet = false;
  isAnimating: boolean = false;

  /**
   * Quantity of the ONE cart line this sheet currently refers to — the line matching the ticked
   * add-ons, or the loaded line in edit mode. Deliberately not the product's total across every
   * line: the bottom bar acts on a single line, so a sum would make it offer to "remove all" of a
   * combination the guest never selected.
   */
  itemCount: number = 0;

  /** Ids of the add-ons currently ticked. Lives here, not in the cart, until the guest commits. */
  selectedSupplementIds = new Set<number>();

  /**
   * The line the bottom bar is acting on, or null in add mode.
   *
   * Seeded from `editingLineKey` and then owned by the guest: tapping a row in "in your order"
   * loads it, "start a new combination" clears it. In add mode the ticked add-ons IDENTIFY which
   * line the bar acts on and the guest may wander between lines by ticking; loaded, the line is
   * fixed by identity and the ticks are its proposed NEW identity.
   */
  loadedLine: CartItem | null = null;

  /**
   * The product's distinct cart lines, recomputed on every cart emission rather than read from a
   * template getter: `getProductLines` allocates a fresh sorted array per call, and a getter bound
   * to *ngFor would allocate one per change-detection pass.
   */
  orderLines: CartItem[] = [];

  // Track loaded images by product id
  loadedImages: Set<number> = new Set();
  errorImages: Set<number> = new Set();

  // Overlay is created/destroyed on every open; keep subscriptions bounded to this instance.
  private subscriptions = new Subscription();

  /** True when rendered as the global overlay (not the routed deep-link instance). */
  @Input() overlayMode = false;

  /**
   * Open pointed at THIS existing cart line (the cart's rows pass it through
   * `ProductSheetService.open(id, lineKey)`). Only the INITIAL value — `loadedLine` is the live
   * answer once the guest starts tapping rows.
   */
  @Input() editingLineKey: string | null = null;

  /** Overlay-driven product id. Setter fires before ngOnInit, so it loads the product first. */
  @Input() set productId(value: number | null) {
    if (value != null) {
      // Defer out of the current change-detection pass: on a cache miss
      // loadProductFromCache -> dismiss() -> sheet.close() re-emits the sheet
      // state that AppComponent's `(sheet.changes$ | async)` just read in this
      // same pass, throwing ExpressionChangedAfterItHasBeenCheckedError (dev) /
      // flashing the sheet open-then-closed (prod).
      this.loading = true;
      Promise.resolve().then(() => this.loadProductFromCache(value));
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cartService: CartService,
    private langService: LanguageService,
    private translate: TranslateService,
    private currencyService: CurrencyService,
    private sheet: ProductSheetService,
    private supplementCatalog: SupplementCatalogService,
    private analytics: AnalyticsService
  ) { }

  ngOnInit() {
    // Route-param load only for the routed deep-link instance; the overlay uses @Input productId.
    if (!this.overlayMode) {
      const productId = this.route.snapshot.paramMap.get('id');
      if (productId) {
        this.loadProductFromCache(parseInt(productId));
      } else {
        this.dismiss();
      }
    }
    this.currencySymbol = sessionStorage.getItem('currencySymbol') || '$';

    // Load clientData for second currency checks
    const clientDataString = sessionStorage.getItem('clientData');
    if (clientDataString) {
      try {
        this.clientData = JSON.parse(clientDataString);
      } catch (e) {
        console.error('Error parsing clientData:', e);
      }
    }

    this.subscriptions.add(
      this.cartService.cartItems$.subscribe(items => {
        this.cartItems = items;
        this.onCartChanged();
      })
    );
    this.currentLang = this.langService.getCurrentLang();

    // Subscribe to language changes
    this.subscriptions.add(
      this.langService.currentLang$.subscribe(lang => {
        this.currentLang = lang;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadProductFromCache(productId: number) {
    this.loading = true;
    const cachedProducts = sessionStorage.getItem('menuProducts');

    if (cachedProducts) {
      try {
        const products: ProductResponse[] = JSON.parse(cachedProducts);
        this.product = products.find(p => p.id === productId) || null;

        if (this.product) {
          this.loading = false;
          // Re-sync quantity now that the product is actually loaded: cartItems$
          // (a BehaviorSubject) already emitted during ngOnInit while product was
          // still null (the load is deferred), so the cart callback's init was a
          // no-op. Also resets quantity when the overlay is reused for a new product.
          this.setupInitialState();
        } else {
          console.error('Product not found in cache');
          this.dismiss();
        }
      } catch (e) {
        console.error('Error parsing cached products:', e);
        this.dismiss();
      }
    } else {
      console.error('No cached products found');
      this.dismiss();
    }
    this.loading = false;
  }

  goBack() {
    this.dismiss();
  }

  /** Overlay mode closes the sheet in place; routed (deep-link) mode navigates home. */
  private dismiss() {
    if (this.overlayMode) {
      this.sheet.close();
    } else {
      this.router.navigate(['/menu']);
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Add-ons
  // ---------------------------------------------------------------------------------------------

  /**
   * The add-ons this venue offers on this dish. Everything downstream derives its selection from
   * this array, so an add-on that has left the menu cannot linger in a selection.
   */
  get supplements(): MenuSupplement[] {
    return this.product?.supplements ?? [];
  }

  /**
   * Whether the picker is DISPLAYED at all. The same expression the tiles branch on — two
   * definitions would let a tile open a sheet that then shows no picker.
   */
  get hasSupplements(): boolean {
    return this.supplementCatalog.productHasSupplements(this.product);
  }

  /**
   * Add-ons are SHOWN even when ordering is off — "Extra cheese +50" is menu information, not an
   * ordering action — but they are not selectable then. Reusing the bar's own gate keeps this in
   * lockstep with it rather than inventing a second notion of "ordering is off".
   */
  get supplementsSelectable(): boolean {
    return this.canEnableOrdering();
  }

  /**
   * The ticked add-ons in the shape the cart stores, resolved through the CURRENT menu so a
   * selection can never carry an add-on the venue has withdrawn.
   */
  get selectedSupplements(): CartSupplement[] {
    return this.supplements
      .filter(supplement => supplement.id != null && this.selectedSupplementIds.has(supplement.id))
      .map(supplement =>
        toCartSupplement(
          supplement,
          this.supplementCatalog.localizedSupplementName(supplement.id!, supplement.name ?? '')
        )
      );
  }

  isSupplementSelected(supplement: MenuSupplement): boolean {
    return supplement.id != null && this.selectedSupplementIds.has(supplement.id);
  }

  /** The add-on's name in the current language, so the picker follows a language switch. */
  supplementName(supplement: MenuSupplement): string {
    if (supplement.id == null) return supplement.name ?? '';
    return this.supplementCatalog.localizedSupplementName(supplement.id, supplement.name ?? '');
  }

  supplementPrice(supplement: MenuSupplement): number {
    const parsed = parseFloat(String(supplement.price ?? '0'));
    return isFinite(parsed) ? parsed : 0;
  }

  toggleSupplement(supplement: MenuSupplement): void {
    if (!this.supplementsSelectable || supplement.id == null) return;

    if (this.selectedSupplementIds.has(supplement.id)) {
      this.selectedSupplementIds.delete(supplement.id);
    } else {
      this.selectedSupplementIds.add(supplement.id);
    }

    // Edit mode is pinned to one line by identity, not by the selection on screen. Re-pointing here
    // would flip the bar to a different line — and to "add to order" — the instant the guest
    // unticks a box, which is exactly the silent-second-line behaviour this screen prevents.
    if (this.loadedLine) return;

    // In add mode the selection changes WHICH line this sheet refers to, so `itemCount` — and with
    // it the add/update/remove decision — has to follow. Deliberately NOT a full `syncItemCount()`:
    // its own `actual !== this.itemCount` guard would skip the stepper entirely on a 0 → 0 hop
    // between two combinations the cart does not hold, which is precisely the case below that needs
    // the reset.
    if (this.product) {
      this.itemCount = this.cartService.getLineQuantity(this.product.id, this.selectedSupplements);
      if (this.itemCount > 0) {
        // A line that exists dictates the stepper.
        this.localQuantity = this.itemCount;
        this.quantityIsGuestSet = false;
      } else if (!this.quantityIsGuestSet) {
        // Nothing in the order matches this selection, and the number on screen was inherited from a
        // line the guest has now navigated away from — it describes an order they never placed. A
        // combination the cart does not hold starts at one. A number they set themselves is left
        // alone: three pizzas, now with cheese, is still three pizzas.
        this.localQuantity = 1;
      }
    }
  }

  // ---------------------------------------------------------------------------------------------
  // "In your order"
  // ---------------------------------------------------------------------------------------------

  /**
   * Shown only for a dish that HAS add-ons: one without them has a single possible line, so there
   * is nothing to tell apart, and the block would spend half the sheet announcing "In your order —
   * No add-ons" beside an offer to start a second combination of a dish that has none.
   */
  get showOrderContext(): boolean {
    return this.hasSupplements && this.orderLines.length > 0;
  }

  get isEditingLine(): boolean {
    return this.loadedLine != null;
  }

  isLoadedLine(line: CartItem): boolean {
    return !!this.loadedLine &&
           this.cartService.getLineKey(line) === this.cartService.getLineKey(this.loadedLine);
  }

  /**
   * How a line's add-on selection reads to a guest — one definition, because the cart names the
   * same line and two spellings of one combination read as two dishes. Names go back through the
   * catalogue rather than using the line's stored snapshot, so they follow a language switch.
   */
  combinationName(line: CartItem): string {
    const names = (line.supplements ?? [])
      .map(supplement =>
        this.supplementCatalog.localizedSupplementName(supplement.id, supplement.name ?? '').trim()
      )
      .filter(name => !!name);
    return names.length > 0 ? names.join(' · ') : this.translate.instant('addOns.none');
  }

  /** Add-on inclusive: the row prices the whole line, not the dish it started as. */
  lineTotal(line: CartItem): number {
    return cartLineTotal(line.price, line.quantity, line.supplements);
  }

  /**
   * Point the bottom bar at an existing line and mirror it into the picker.
   *
   * Deliberately unguarded by `supplementsSelectable` — the row's button carries `disabled`, which
   * is the one place a browse-only menu is enforced. A guard here would also swallow the seed from
   * `editingLineKey` on the routed instance, where `clientData` is not parsed yet when the product
   * loads, and the sheet would open in add mode on a line the guest asked to edit.
   */
  loadLine(line: CartItem): void {
    // Intersect with the CURRENT menu. `selectedSupplements` is built by filtering
    // `product.supplements`, so an add-on that has since left the menu has no row to tick and would
    // silently vanish from the saved selection — keeping its id here would let the bar claim "no
    // changes" while saving quietly dropped it.
    const onMenu = new Set(
      this.supplements
        .map(supplement => supplement.id)
        .filter((id): id is number => id != null)
    );
    this.selectedSupplementIds = new Set(
      (line.supplements ?? []).map(supplement => supplement.id).filter(id => onMenu.has(id))
    );
    this.itemCount = line.quantity;
    this.localQuantity = Math.max(this.itemCount, 1);
    // The loaded line's own count, not the guest's — so moving off this combination resets it.
    this.quantityIsGuestSet = false;
    this.loadedLine = line;
  }

  /** Leave edit mode: blank ticks, back to add-mode semantics. */
  startNewCombination(): void {
    this.loadedLine = null;
    this.selectedSupplementIds = new Set();
    this.localQuantity = 1;
    this.quantityIsGuestSet = false;
    this.itemCount = 0;
    this.syncItemCount();
  }

  /**
   * A row's own stepper acts on that exact line, so it needs no mode switch and never touches the
   * bottom bar. Dropping to zero routes through the cart's own remove, which is why there is no
   * separate reset control here.
   */
  adjustLineQuantity(line: CartItem, change: number): void {
    if (!this.supplementsSelectable) return;
    this.cartService.updateLineQuantity(
      this.cartService.getLineKey(line),
      line.quantity + change,
      'product_details'
    );
  }

  trackLine = (_index: number, line: CartItem): string => this.cartService.getLineKey(line);

  trackSupplement = (index: number, supplement: MenuSupplement): number => supplement.id ?? index;

  // ---------------------------------------------------------------------------------------------
  // Quantity + the bottom bar
  // ---------------------------------------------------------------------------------------------

  // Update local quantity with animation
  updateLocalQuantity(change: number): void {
    if (this.isAnimating) return;

    const newQuantity = this.localQuantity + change;
    if (newQuantity >= 1) {
      this.localQuantity = newQuantity;
      // The guest typed this one, so a later selection change must leave it alone.
      this.quantityIsGuestSet = true;
      this.triggerQuantityAnimation();
    }
  }

  // Trigger zoom animation for quantity change
  private triggerQuantityAnimation(): void {
    this.isAnimating = true;
    setTimeout(() => {
      this.isAnimating = false;
    }, 200);
  }

  /** The dish alone, per unit. Add-ons are priced on top of it, never folded into it. */
  /**
   * The dish's own price, chosen the same way `CartService.addToCart` chooses the price it STORES on
   * the line: `on_sale ? sale_price : regular_price`.
   *
   * It used to read `product.price`, which was harmless while the bar was the only figure on the
   * sheet — nothing else was there to contradict it. The "in your order" rows changed that: they
   * price the same dish from the line the cart actually holds, so the two now sit inches apart and
   * any payload where `price` diverges from the effective price puts two different numbers for one
   * dish on one screen. Reading it the cart's way makes disagreement impossible rather than
   * unlikely.
   *
   * For every venue where `price` already equals the effective price — which is what WooCommerce
   * normally serves — this changes nothing at all.
   */
  private get basePrice(): number {
    const effective = this.product?.on_sale ? this.product?.sale_price : this.product?.regular_price;
    // `price` remains the fallback: a payload that omits regular_price would otherwise zero the bar.
    const parsed = parseFloat(String(effective ?? this.product?.price ?? '0'));
    return isFinite(parsed) ? parsed : 0;
  }

  /**
   * What the bar charges: add-on INCLUSIVE. The second-currency figure beside it is a pure multiply
   * of whatever it is handed, so a base-only number here would make the converted amount disagree
   * with the primary one.
   */
  get barTotal(): number {
    return cartLineTotal(this.basePrice, this.localQuantity, this.selectedSupplements);
  }

  /**
   * Whether the guest has actually altered the line they opened.
   *
   * Compared on the line KEY, not on the set of ticked ids: an add-on carries its own quantity, so
   * {cheese ×2} and {cheese ×1} are different lines with identical ids.
   */
  get hasEditChanges(): boolean {
    if (!this.loadedLine || !this.product) return false;
    const newKey = cartLineKey(this.product.id, this.selectedSupplements);
    return newKey !== this.cartService.getLineKey(this.loadedLine) ||
           this.localQuantity !== this.loadedLine.quantity;
  }

  /**
   * In edit mode the bar's state comes from the EDIT, never from "is the on-screen selection in the
   * cart" — reading the latter would flip the bar to "add to order" the moment the guest unticks a
   * box, on a line that is still sitting in their order.
   */
  get cartAction(): CartAction {
    if (this.isEditingLine) return this.hasEditChanges ? 'update' : 'remove';
    if (this.itemCount <= 0) return 'add';
    return this.localQuantity !== this.itemCount ? 'update' : 'remove';
  }

  // Get cart action button text
  getCartActionText(): string {
    if (!this.product) return '';

    switch (this.cartAction) {
      case 'add':
        return this.translate.instant('CART.ADD_TO_ORDER');
      case 'update':
        // Edit mode reads differently on purpose: "update order" alongside a pre-ticked selection
        // suggests adding more of it, which is the opposite of what happens.
        return this.translate.instant(this.isEditingLine ? 'addOns.saveChanges' : 'CART.UPDATE_ORDER');
      case 'remove':
        return this.translate.instant(this.isEditingLine ? 'addOns.removeFromOrder' : 'CART.REMOVE_ALL');
    }
  }

  // Check if cart action button should be red (remove)
  isRemoveAllAction(): boolean {
    if (!this.product) return false;
    return this.cartAction === 'remove';
  }

  // Handle cart action button click
  handleCartAction(event: Event, product: ProductResponse): void {
    event.stopPropagation();

    // Every call below is scoped to ONE line — the ticked selection's, or the loaded one's — so the
    // same dish under a different selection is left alone.
    const chosen = this.selectedSupplements;

    switch (this.cartAction) {
      case 'add':
        this.cartService.addToCart(product, 'productDetails', chosen, this.localQuantity);
        break;
      case 'update':
        if (this.loadedLine) {
          // Not an add: `changeSupplements` takes the quantity off the old line and gives it to the
          // new one, so the guest ends with the number of dishes they started with unless they
          // moved the stepper too.
          this.cartService.changeSupplements(this.loadedLine, chosen, this.localQuantity);
        } else {
          this.cartService.updateLineQuantity(
            cartLineKey(product.id, chosen),
            this.localQuantity,
            'product_details'
          );
        }
        break;
      case 'remove':
        this.cartService.removeLine(
          this.loadedLine
            ? this.cartService.getLineKey(this.loadedLine)
            : cartLineKey(product.id, chosen),
          'productDetails'
        );
        break;
    }

    // Close product details
    this.goBack();
  }

  // ---------------------------------------------------------------------------------------------
  // Cart state plumbing
  // ---------------------------------------------------------------------------------------------

  private setupInitialState(): void {
    if (!this.product) return;

    // A fresh slate first: the routed instance is reused across deep-links and the overlay is
    // re-created per open, so nothing may survive from a previous product.
    this.loadedLine = null;
    this.selectedSupplementIds = new Set();
    this.itemCount = 0;
    this.localQuantity = 1;
    this.quantityIsGuestSet = false;
    this.recomputeOrderLines();

    const editingKey = this.resolveEditingLineKey();
    const editingLine = editingKey
      ? this.orderLines.find(line => this.cartService.getLineKey(line) === editingKey)
      : undefined;

    if (editingLine) {
      this.loadLine(editingLine);
    } else {
      this.syncItemCount();
    }

    // Only worth reporting when there is something to disambiguate: one line is the pre-add-ons
    // world, and logging it would drown the signal this event exists to measure.
    if (this.orderLines.length > 1) {
      this.analytics.log('supplement_combinations_shown', {
        product_id: this.product.id.toString(),
        combination_count: this.orderLines.length
      });
    }
  }

  /**
   * The line key the sheet was opened on.
   *
   * Prefers the `@Input`, which is the contract the cart's rows are written against. Falls back to
   * the service in overlay mode because AppComponent hosts the overlay and binds only `productId`
   * today — without the fallback an `open(id, lineKey)` would land on a blank picker, i.e. exactly
   * the silent-second-line bug the argument exists to prevent.
   */
  private resolveEditingLineKey(): string | null {
    if (this.editingLineKey) return this.editingLineKey;
    return this.overlayMode ? this.sheet.editingLineKey : null;
  }

  private onCartChanged(): void {
    if (!this.product) return;
    this.recomputeOrderLines();

    if (this.loadedLine) {
      const key = this.cartService.getLineKey(this.loadedLine);
      const live = this.orderLines.find(line => this.cartService.getLineKey(line) === key);
      if (!live) {
        // The loaded line is gone — its own stepper drove it to zero, or another surface removed
        // it. Staying pinned would leave "save changes" pointing at nothing, and saving would
        // resurrect a line the guest just deleted.
        this.startNewCombination();
        return;
      }
      // Stay pinned, but on the LIVE row: the sheet may be open while a stepper elsewhere moves
      // this line, and `hasEditChanges` compares against its quantity.
      this.loadedLine = live;
      return;
    }

    this.syncItemCount();
  }

  private recomputeOrderLines(): void {
    this.orderLines = this.product ? this.cartService.getProductLines(this.product.id) : [];
  }

  /**
   * Re-read the quantity of the line the current selection points at.
   *
   * Guarded on an actual change rather than assigning unconditionally: an unrelated cart emission
   * (another dish added elsewhere) must not stamp on a stepper the guest has already moved. For a
   * dish with no add-ons the selection is empty, the key is `"<id>|"`, and this reads exactly the
   * single line it always did.
   */
  private syncItemCount(): void {
    if (!this.product) return;
    const actual = this.cartService.getLineQuantity(this.product.id, this.selectedSupplements);
    if (actual !== this.itemCount) {
      this.itemCount = actual;
      // Either way the number is the cart's, not the guest's, so it must not be protected from a
      // later selection change.
      this.localQuantity = actual > 0 ? actual : 1;
      this.quantityIsGuestSet = false;
    }
  }

  getProductName(product: ProductResponse, _lang?: string): string {
    return this.langService.localizeMetaValue(product.meta_data, '', product.name);
  }

  getProductDisplayDescription(product: ProductResponse, _lang?: string): string {
    return this.langService.localizeMetaValue(product.meta_data, 'description', product.description);
  }

  hasValidImage(product: ProductResponse): boolean {
    return !!(product.images && product.images.length > 0 && product.images[0] && product.images[0].src);
  }

  getProductImageSrc(product: ProductResponse): string {
    if (this.hasValidImage(product)) {
      return product.images[0].src;
    }
    return '../../../assets/product-details-placeholder.png';
  }

  onImageLoad(productId: number) {
    this.loadedImages.add(productId);
  }

  onImageError(productId: number) {
    this.errorImages.add(productId);
  }

  isImageLoaded(productId: number): boolean {
    return this.loadedImages.has(productId);
  }

  isImageError(productId: number): boolean {
    return this.errorImages.has(productId);
  }

  getSecondPrice(price: string | number): string | null {
    return this.currencyService.getSecondPrice(price, this.clientData);
  }

  getSecondCurrencySymbol(): string | null {
    return this.currencyService.getSecondCurrencySymbol(this.clientData);
  }

  isSecondCurrencyActive(): boolean {
    return this.currencyService.isSecondCurrencyActive(this.clientData);
  }

  canEnableOrdering(): boolean {
    const orderingType = sessionStorage.getItem('orderingType');

    // Rule 1: Table QR (dine-in)
    if (orderingType === 'dine-in') {
      return this.clientData?.feature_flags?.ft_orders_from_table === true &&
             this.isOrderingSupportedByExistingLogic();
    }

    // Rule 2: Main QR or Order Location (off-site/delivery)
    if (orderingType === 'off-site' || orderingType === 'delivery') {
      return this.clientData?.feature_flags?.ft_orders_from_home === true &&
             this.isOrderingSupportedByExistingLogic();
    }

    return false;
  }

  private isOrderingSupportedByExistingLogic(): boolean {
    // Match iOS isOrderingEnabled exactly: is_ordering_active must be true (nil/false → false), is_open_now/is_order_supported nil = OK.
    return this.clientData?.is_ordering_active === true &&
           (this.clientData?.is_open_now !== false) &&
           (this.clientData?.is_order_supported !== false);
  }

}
